import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { CapacitorHttp } from '@capacitor/core';
import { setCurrentApiBaseUrl } from '../api/config';

const API_URL_KEY = 'api_base_url';

// Default API URLs basierend auf Platform
const getDefaultApiUrl = () => {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  if (isNative) {
    if (platform === 'android') {
      return 'http://10.0.2.2:86'; // Android Emulator
    }
    if (platform === 'ios') {
      return 'http://localhost:86'; // iOS Simulator
    }
  }

  // Web default
  return 'https://api.billard.flogra.de';
};

/**
 * Hook to manage API Base URL configuration
 * Persists across app restarts using Capacitor Preferences
 */
export const useApiConfig = () => {
  const [apiUrl, setApiUrl] = useState(getDefaultApiUrl());
  const [isLoading, setIsLoading] = useState(true);

  // Load saved API URL on mount
  useEffect(() => {
    loadApiUrl();
  }, []);

  const loadApiUrl = async () => {
    try {
      const { value } = await Preferences.get({ key: API_URL_KEY });
      if (value) {
        setApiUrl(value);
        setCurrentApiBaseUrl(value);
        console.log('Loaded API URL from storage:', value);
      } else {
        const defaultUrl = getDefaultApiUrl();
        setCurrentApiBaseUrl(defaultUrl);
        console.log('Using default API URL:', defaultUrl);
      }
    } catch (error) {
      console.error('Error loading API URL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveApiUrl = async (newUrl) => {
    try {
      // Validate URL format
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(newUrl)) {
        throw new Error('Invalid URL format. Must start with http:// or https://');
      }

      // Remove trailing slash
      const cleanUrl = newUrl.replace(/\/$/, '');

      await Preferences.set({
        key: API_URL_KEY,
        value: cleanUrl,
      });

      setApiUrl(cleanUrl);
      // Update the current API base URL immediately so API client picks it up
      setCurrentApiBaseUrl(cleanUrl);
      console.log('Saved API URL:', cleanUrl);

      return { success: true };
    } catch (error) {
      console.error('Error saving API URL:', error);
      return { success: false, error: error.message };
    }
  };

  const resetToDefault = async () => {
    const defaultUrl = getDefaultApiUrl();
    await Preferences.remove({ key: API_URL_KEY });
    setApiUrl(defaultUrl);
    // Update the current API base URL immediately
    setCurrentApiBaseUrl(defaultUrl);
    console.log('Reset API URL to default:', defaultUrl);
    return { success: true };
  };

  const testConnection = async (url) => {
    try {
      const testUrl = url || apiUrl;
      // Use players endpoint - if server responds (even with 401), connection works
      const fullUrl = `${testUrl}/api/players/`;

      let response;

      if (Capacitor.isNativePlatform()) {
        // Use CapacitorHttp for native platforms
        console.log('Testing connection with CapacitorHttp:', fullUrl);
        response = await CapacitorHttp.request({
          url: fullUrl,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Accept 2xx, 401, and 403 as successful connection (server is responding)
        const isSuccess = (response.status >= 200 && response.status < 300) ||
                         response.status === 401 ||
                         response.status === 403;

        return {
          success: isSuccess,
          status: response.status,
          message: isSuccess
            ? 'Connection successful'
            : `Server error: HTTP ${response.status}`,
        };
      } else {
        // Use fetch for web platforms
        console.log('Testing connection with fetch:', fullUrl);
        response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Accept 2xx, 401, and 403 as successful connection (server is responding)
        const isSuccess = response.ok || response.status === 401 || response.status === 403;

        return {
          success: isSuccess,
          status: response.status,
          message: isSuccess
            ? 'Connection successful'
            : `Server error: HTTP ${response.status}`,
        };
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      return {
        success: false,
        status: 0,
        message: error.message || 'Connection failed - check URL and network',
      };
    }
  };

  return {
    apiUrl,
    isLoading,
    saveApiUrl,
    resetToDefault,
    testConnection,
    defaultUrl: getDefaultApiUrl(),
  };
};

/**
 * Get the current API URL synchronously
 * Falls back to default if not set
 */
export const getApiUrl = async () => {
  try {
    const { value } = await Preferences.get({ key: API_URL_KEY });
    return value || getDefaultApiUrl();
  } catch (error) {
    console.error('Error getting API URL:', error);
    return getDefaultApiUrl();
  }
};
